import { showUserFriendlyNotyf, __ } from "../../utils/notyf-helpers.js";
import simpleCacheManager from "../../shared/cache-manager.js";

/**
 * @file Industry Collection Module
 * @description Handles industry collection management functionality
 */

export class IndustryCollection {
  constructor() {
    this.collection = new Set();
    this.isInitialized = false;
    this.collectionContainer = null;
  }

  /**
   * Initialize the industry collection
   */
  async init() {
    console.debug("Industry Collection: Initializing...");

    try {
      this.loadCollectionFromStorage();
      this.setupEventListeners();
      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error("Industry Collection: Initialization failed:", error);
      return false;
    }
  }

  /**
   * Load collection from storage
   */
  loadCollectionFromStorage() {
    try {
      // Use SimpleCacheManager for user data (persistent)
      const stored = simpleCacheManager.get("industry_collection", "user");
      if (stored) {
        this.collection = new Set(stored);
      }
    } catch (error) {
      console.error("Industry Collection: Error loading from storage:", error);
      this.collection = new Set();
    }
  }

  /**
   * Save collection to storage
   */
  saveCollectionToStorage() {
    try {
      const items = Array.from(this.collection);
      simpleCacheManager.set("industry_collection", items, null, "user"); // null TTL = persistent
    } catch (error) {
      console.error("Industry Collection: Error saving to storage:", error);
    }
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Listen for add to collection events
    document.addEventListener("add-to-collection", (e) => {
      const { itemId, itemType } = e.detail;
      if (itemType === "industry") {
        this.addToCollection(itemId);
      }
    });

    // Listen for remove from collection events
    document.addEventListener("remove-from-collection", (e) => {
      const { itemId, itemType } = e.detail;
      if (itemType === "industry") {
        this.removeFromCollection(itemId);
      }
    });

    // Listen for collection display events
    document.addEventListener("show-collection", () => {
      this.showCollection();
    });
  }

  /**
   * Add item to collection
   */
  addToCollection(itemId) {
    if (this.collection.has(itemId)) {
      showUserFriendlyNotyf(
        __("This industrial park is already in your collection", "iphub"),
        "info",
      );
      return;
    }

    this.collection.add(itemId);
    this.saveCollectionToStorage();

    showUserFriendlyNotyf(__("Industrial park added to collection", "iphub"), "success");

    // Update UI
    this.updateCollectionUI();

    // Trigger custom event
    const event = new CustomEvent("collection-updated", {
      detail: {
        action: "added",
        itemId,
        collectionSize: this.collection.size,
      },
    });
    document.dispatchEvent(event);
  }

  /**
   * Remove item from collection
   */
  removeFromCollection(itemId) {
    if (!this.collection.has(itemId)) {
      return;
    }

    this.collection.delete(itemId);
    this.saveCollectionToStorage();

    showUserFriendlyNotyf(__("Industrial park removed from collection", "iphub"), "success");

    // Update UI
    this.updateCollectionUI();

    // Trigger custom event
    const event = new CustomEvent("collection-updated", {
      detail: {
        action: "removed",
        itemId,
        collectionSize: this.collection.size,
      },
    });
    document.dispatchEvent(event);
  }

  /**
   * Check if item is in collection
   */
  isInCollection(itemId) {
    return this.collection.has(itemId);
  }

  /**
   * Get collection items
   */
  getCollectionItems() {
    return Array.from(this.collection);
  }

  /**
   * Get collection size
   */
  getCollectionSize() {
    return this.collection.size;
  }

  /**
   * Clear collection
   */
  clearCollection() {
    this.collection.clear();
    this.saveCollectionToStorage();
    this.updateCollectionUI();

    showUserFriendlyNotyf(__("Collection cleared", "iphub"), "success");
  }

  /**
   * Show collection
   */
  async showCollection() {
    const container = document.getElementById("collection-container");
    if (!container) {
      console.warn("Industry Collection: No container found for displaying collection");
      return;
    }

    if (this.collection.size === 0) {
      container.innerHTML = `
        <div class="empty-collection">
          <p>Your collection is empty.</p>
          <p>Add industrial parks to your collection to see them here.</p>
        </div>
      `;
      return;
    }

    try {
      this.showLoadingState(container);

      const items = await this.fetchCollectionItems();
      this.displayCollectionItems(container, items);
    } catch (error) {
      console.error("Industry Collection: Error loading collection items:", error);
      this.showErrorState(container);
    }
  }

  /**
   * Fetch collection items from API
   */
  async fetchCollectionItems() {
    const itemIds = Array.from(this.collection);
    const promises = itemIds.map((id) =>
      fetch(`/wp-json/wp/v2/industry/${id}`).then((res) => res.json()),
    );

    const items = await Promise.all(promises);
    return items.filter((item) => item.id); // Filter out any failed requests
  }

  /**
   * Display collection items
   */
  displayCollectionItems(container, items) {
    container.innerHTML = `
      <div class="collection-header">
        <h3>Your Collection (${items.length})</h3>
        <button class="clear-collection">Clear Collection</button>
      </div>
      <div class="collection-items">
        ${items.map((item) => this.createCollectionItemHTML(item)).join("")}
      </div>
    `;

    // Add event listeners
    const clearBtn = container.querySelector(".clear-collection");
    if (clearBtn) {
      clearBtn.addEventListener("click", () => {
        this.clearCollection();
        this.showCollection();
      });
    }

    // Add remove buttons
    container.querySelectorAll(".remove-from-collection").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const itemId = e.target.dataset.itemId;
        this.removeFromCollection(itemId);
        this.showCollection(); // Refresh display
      });
    });
  }

  /**
   * Create collection item HTML
   */
  createCollectionItemHTML(item) {
    return `
      <div class="collection-item" data-id="${item.id}">
        <div class="item-content">
          <h4 class="item-title">
            <a href="${item.link}">${item.title.rendered}</a>
          </h4>
          <div class="item-meta">
            ${item.meta?.location ? `<span class="location">${item.meta.location}</span>` : ""}
            ${item.meta?.area ? `<span class="area">${item.meta.area}</span>` : ""}
          </div>
          <div class="item-excerpt">
            ${item.excerpt.rendered}
          </div>
        </div>
        <div class="item-actions">
          <a href="${item.link}" class="view-details">View Details</a>
          <button class="remove-from-collection" data-item-id="${item.id}">Remove</button>
        </div>
      </div>
    `;
  }

  /**
   * Show loading state
   */
  showLoadingState(container) {
    container.innerHTML = `
      <div class="loading-state">
        <div class="spinner"></div>
        <p>Loading your collection...</p>
      </div>
    `;
  }

  /**
   * Show error state
   */
  showErrorState(container) {
    container.innerHTML = `
      <div class="error-state">
        <p>An error occurred while loading your collection.</p>
        <button class="retry-loading">Retry</button>
      </div>
    `;

    const retryBtn = container.querySelector(".retry-loading");
    if (retryBtn) {
      retryBtn.addEventListener("click", () => {
        this.showCollection();
      });
    }
  }

  /**
   * Update collection UI elements
   */
  updateCollectionUI() {
    // Update collection count
    const countElements = document.querySelectorAll(".collection-count");
    countElements.forEach((element) => {
      element.textContent = this.collection.size;
    });

    // Update add/remove buttons
    document.querySelectorAll("[data-collection-action]").forEach((button) => {
      const itemId = button.dataset.itemId;
      const action = button.dataset.collectionAction;

      if (action === "add" && this.isInCollection(itemId)) {
        button.textContent = "In Collection";
        button.disabled = true;
      } else if (action === "remove" && !this.isInCollection(itemId)) {
        button.textContent = "Add to Collection";
        button.dataset.collectionAction = "add";
      }
    });
  }

  /**
   * Export collection
   */
  exportCollection() {
    const items = Array.from(this.collection);
    const data = {
      collection: items,
      exportDate: new Date().toISOString(),
      totalItems: items.length,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `iphub-collection-${new Date().toISOString().split("T")[0]}.json`;
    a.click();

    URL.revokeObjectURL(url);
  }

  /**
   * Import collection
   */
  importCollection(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          if (data.collection && Array.isArray(data.collection)) {
            this.collection = new Set(data.collection);
            this.saveCollectionToStorage();
            this.updateCollectionUI();

            showUserFriendlyNotyf(__("Collection imported successfully", "iphub"), "success");

            resolve(data.collection);
          } else {
            reject(new Error("Invalid collection format"));
          }
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsText(file);
    });
  }

  /**
   * Get collection statistics
   */
  getCollectionStats() {
    return {
      totalItems: this.collection.size,
      items: Array.from(this.collection),
      lastUpdated: new Date().toISOString(),
    };
  }

  /**
   * Destroy the collection instance
   */
  destroy() {
    this.collection.clear();
    this.isInitialized = false;
  }
}

/**
 * Initialize industry collection
 */
export function initIndustryCollection() {
  const collection = new IndustryCollection();
  return collection.init().then((success) => {
    if (success) {
      // Store instance globally for access
      window.IPHUB_INDUSTRY_COLLECTION = collection;
    }
    return collection;
  });
}
